import { ApolloError, useMutation } from "@apollo/client";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography } from "@mui/material";
import { useState } from "react";
import { useNotify, useTranslate } from "react-admin";
import { useNavigate } from "react-router-dom";

import {
  CreateTestLicensingCustomerDocument,
  type CreateTestLicensingCustomerMutation,
  type CreateTestLicensingCustomerMutationVariables,
  CrmObject,
} from "../generated/graphql";
import { crmRecordPath } from "../lib/crm";
import { space } from "../theme/designTokens";
import { dialogContentSx, dialogTitleSx } from "../theme/dialogStyles";

const EMAIL_FORMAT = /^\S+@\S+\.\S+$/;

// Outside production there is no real Salesforce account to invite a tester
// from, so staff add a test B2B account with the tester as licence manager and
// invite them from its page.
export const TestAccountDialog = ({ onClose }: { onClose: () => void }) => {
  const translate = useTranslate();
  const notify = useNotify();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [organizationName, setOrganizationName] = useState("");
  const [managerName, setManagerName] = useState("");
  const [create, { loading }] = useMutation<
    CreateTestLicensingCustomerMutation,
    CreateTestLicensingCustomerMutationVariables
  >(CreateTestLicensingCustomerDocument);
  const complete = EMAIL_FORMAT.test(email.trim()) && organizationName.trim() !== "" && managerName.trim() !== "";

  const save = async () => {
    try {
      const { data } = await create({
        variables: { email: email.trim(), organizationName: organizationName.trim(), managerName: managerName.trim() },
      });
      const accountId = data?.createTestLicensingCustomer;
      if (!accountId) throw new Error("Missing test account");
      notify(translate("testAccount.success"), { type: "success" });
      navigate(crmRecordPath(CrmObject.Account, accountId));
    } catch (error) {
      const code = error instanceof ApolloError ? error.graphQLErrors[0]?.message : undefined;
      notify(translate(code === "customer-already-exists" ? "testAccount.alreadyExists" : "testAccount.error"), {
        type: "error",
      });
    }
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm" aria-labelledby="test-account-title">
      <DialogTitle id="test-account-title" sx={dialogTitleSx}>
        {translate("testAccount.title")}
      </DialogTitle>
      <DialogContent sx={dialogContentSx}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {translate("testAccount.hint")}
        </Typography>
        <TextField
          label={translate("testAccount.email")}
          value={email}
          onChange={event => setEmail(event.target.value)}
          fullWidth
          margin="dense"
          required
        />
        <TextField
          label={translate("testAccount.organizationName")}
          value={organizationName}
          onChange={event => setOrganizationName(event.target.value)}
          fullWidth
          margin="dense"
          required
        />
        <TextField
          label={translate("testAccount.managerName")}
          value={managerName}
          onChange={event => setManagerName(event.target.value)}
          fullWidth
          margin="dense"
          required
        />
      </DialogContent>
      <DialogActions sx={{ px: `${space.xxl}px`, pb: `${space.xl}px` }}>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>
          {translate("testAccount.cancel")}
        </Button>
        <Button variant="contained" onClick={save} disabled={!complete || loading} sx={{ textTransform: "none" }}>
          {translate("testAccount.create")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
